import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Shield, Settings, X, Check } from 'lucide-react';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    functional: false,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie-consent', JSON.stringify(allAccepted));
    setShowBanner(false);
  };

  const handleAcceptSelected = () => {
    const selected = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie-consent', JSON.stringify(selected));
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie-consent', JSON.stringify(onlyNecessary));
    setShowBanner(false);
  };

  const cookieTypes = [
    {
      id: 'necessary',
      name: 'Notwendige Cookies',
      description: 'Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.',
      required: true
    },
    {
      id: 'functional',
      name: 'Funktionale Cookies',
      description: 'Diese Cookies ermöglichen erweiterte Funktionen wie Personalisierung und Speicherung Ihrer Einstellungen.'
    },
    {
      id: 'analytics',
      name: 'Analyse-Cookies',
      description: 'Diese Cookies helfen uns zu verstehen, wie Besucher unsere Website nutzen, um sie zu verbessern.'
    },
    {
      id: 'marketing',
      name: 'Marketing-Cookies',
      description: 'Diese Cookies werden verwendet, um Ihnen relevante Werbung basierend auf Ihren Interessen anzuzeigen.'
    }
  ];

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      {/* Backdrop for settings modal */}
      {showSettings && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-[9998]"
          onClick={() => setShowSettings(false)}
        />
      )}

      {/* Main Banner */}
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
        data-testid="cookie-consent-banner"
      >
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl border border-border/50 overflow-hidden">
            {!showSettings ? (
              /* Simple Banner View */
              <div className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Cookie className="w-7 h-7 text-primary" />
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-foreground mb-2">
                      Wir respektieren Ihre Privatsphäre
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      Wir verwenden Cookies, um Ihre Erfahrung auf unserer Website zu verbessern. 
                      Einige Cookies sind notwendig für die Funktion der Website, während andere uns 
                      helfen, die Website zu verbessern und Ihnen personalisierte Inhalte anzubieten.
                      Weitere Informationen finden Sie in unserer{' '}
                      <a href="/datenschutz" className="text-primary hover:underline">
                        Datenschutzerklärung
                      </a>.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                    <button
                      onClick={() => setShowSettings(true)}
                      className="px-5 py-2.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors flex items-center justify-center gap-2"
                      data-testid="cookie-settings-btn"
                    >
                      <Settings className="w-4 h-4" />
                      Einstellungen
                    </button>
                    <button
                      onClick={handleRejectAll}
                      className="px-5 py-2.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
                      data-testid="cookie-reject-btn"
                    >
                      Nur Notwendige
                    </button>
                    <button
                      onClick={handleAcceptAll}
                      className="px-5 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                      data-testid="cookie-accept-all-btn"
                    >
                      <Check className="w-4 h-4" />
                      Alle akzeptieren
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Settings View */
              <div className="p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-foreground">Cookie-Einstellungen</h3>
                      <p className="text-sm text-muted-foreground">Verwalten Sie Ihre Präferenzen</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-muted-foreground" />
                  </button>
                </div>

                <div className="space-y-4 mb-6 max-h-[40vh] overflow-y-auto">
                  {cookieTypes.map((cookie) => (
                    <div
                      key={cookie.id}
                      className="flex items-start gap-4 p-4 bg-muted/30 rounded-xl"
                    >
                      <div className="flex-shrink-0 pt-1">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={preferences[cookie.id]}
                            onChange={(e) => {
                              if (!cookie.required) {
                                setPreferences(prev => ({
                                  ...prev,
                                  [cookie.id]: e.target.checked
                                }));
                              }
                            }}
                            disabled={cookie.required}
                            className="sr-only peer"
                          />
                          <div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all ${
                            cookie.required 
                              ? 'bg-primary cursor-not-allowed' 
                              : 'bg-gray-300 peer-checked:bg-primary'
                          }`}></div>
                        </label>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-foreground">{cookie.name}</h4>
                          {cookie.required && (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                              Erforderlich
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {cookie.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                  <button
                    onClick={handleRejectAll}
                    className="flex-1 px-5 py-2.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
                  >
                    Nur Notwendige
                  </button>
                  <button
                    onClick={handleAcceptSelected}
                    className="flex-1 px-5 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                    data-testid="cookie-save-settings-btn"
                  >
                    Auswahl speichern
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="flex-1 px-5 py-2.5 bg-secondary text-white rounded-lg text-sm font-medium hover:bg-secondary/90 transition-colors"
                  >
                    Alle akzeptieren
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;
